import { FieldOptionBase } from 'module/api/field/types';
import { useTranslate, useTranslation } from 'module/hooks/use-translation';
import { UserTag } from 'module/ui/components/atoms/select';
import { Tag } from 'module/ui/components/atoms/tag';
import { SingleLineEllipsis } from 'module/ui/components/styles';
import { drop, take } from 'ramda';
import { FC, ReactNode } from 'react';
import styled from 'styled-components';

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

export const HoverableUserTag = styled(UserTag)`
  cursor: pointer !important;
`;

export const HoverableTag = styled(Tag)`
  cursor: pointer !important;
`;
const TriggerOption = styled(HoverableTag)`
  max-width: 22ch;
  display: block;
  ${SingleLineEllipsis};
`;

export const Placeholder = styled.span`
  padding-left: var(--spacing-03);
`;

export interface TriggerOptionProps {
  option: FieldOptionBase<number>;
}

export const UserTriggerOption: FC<
  React.PropsWithChildren<TriggerOptionProps>
> = ({ option }) => <HoverableUserTag {...option} />;
export const TagTriggerOption: FC<
  React.PropsWithChildren<TriggerOptionProps>
> = ({ option: { color, label } }) => (
  <TriggerOption color={color}>{label}</TriggerOption>
);

export type RenderFunction = (
  items: ReactNode[],
  show: number,
  placeholder: ReactNode,
  translate: ReturnType<typeof useTranslate>,
) => ReactNode;

type Props = {
  items: ReactNode[];
  show?: number;
  placeholder?: ReactNode;
  render?: RenderFunction;
};

const defaultRenderFunction: RenderFunction = (
  items,
  show,
  placeholder,
  translate,
) => {
  const count = drop(show)(items).length;
  const other = translate('editor.plus_n_more', { count });

  return (
    <Wrapper>
      {items.length === 0 ? (
        <Placeholder>{placeholder}</Placeholder>
      ) : (
        <>
          {take(show)(items)}
          {count === 0 ? null : (
            <HoverableTag color="cyan">{other}</HoverableTag>
          )}
        </>
      )}
    </Wrapper>
  );
};

export const TriggerContent: FC<React.PropsWithChildren<Props>> = ({
  items,
  show = 2,
  placeholder,
  render = defaultRenderFunction,
}) => {
  const fallback_placeholder = useTranslation('select_with_ellipsis');
  const translate = useTranslate();

  return (
    <>{render(items, show, placeholder ?? fallback_placeholder, translate)}</>
  );
};
